import { ProjectStatus } from '@/modules/projects/types/project';

import {
  PLANNER_AVAILABILITY_FILTER_KEY,
  PLANNER_BILLABLE_AVAILABILITY_FILTER_KEY,
  PLANNER_DATE_RANGE_FILTER_KEY,
  PLANNER_PROJECT_PROBABILITY_FILTER_KEY,
  PLANNER_PROJECT_STATUS_FILTER_KEY,
} from '../../constants/filter-keys';
import { PlannerFilters } from '../types';

/**
 * Pure filter-cascade rules. Some planner filters are GATED on others:
 * - availability + billableAvailability require the date range (backend code 2011)
 * - probability requires projectStatus to include TENTATIVE
 *
 * Removing a gate must cascade-clear its dependents INSIDE the same store update
 * (one filters change → one refetch), and hydrating a dependent without its gate
 * must drop it (it would render as an un-removable locked chip).
 */

/** True when a filter value holds at least one selection. */
export const hasFilterSelection = (value: string | string[] | undefined): boolean =>
  Array.isArray(value) ? value.length > 0 : !!value;

/**
 * Drops dependent filter keys when their gate is unsatisfied — a stale/hand-edited
 * URL or localStorage could otherwise hydrate a gated filter as an un-removable
 * locked chip. No-op unless at least one dependent key actually has a value.
 */
export const dropOrphanedFilters = (
  filters: PlannerFilters,
  dependentKeys: string[],
  isGateSatisfied: boolean
): PlannerFilters => {
  const hasOrphan = !isGateSatisfied && dependentKeys.some(key => hasFilterSelection(filters[key]));
  if (!hasOrphan) return filters;
  return Object.fromEntries(
    Object.entries(filters).filter(([key]) => !dependentKeys.includes(key))
  );
};

export interface FilterCascadeResult {
  /** The next filters object with the change and any cascade clears applied */
  nextFilters: PlannerFilters;
  /** True when clearing the date range cascade-cleared the availability filters */
  clearedAvailability: boolean;
  /** True when removing TENTATIVE cascade-cleared the probability filter */
  clearedProbability: boolean;
}

/**
 * Applies a single filter change plus its cascade rules, PURELY — no toasts, no
 * store access. The caller applies `nextFilters` in one set() and surfaces the
 * cleared* flags (the toast effect turns them into user-facing info toasts).
 */
export const applyFilterCascades = (
  filters: PlannerFilters,
  key: string,
  value: string | string[]
): FilterCascadeResult => {
  const isClearingDateRange =
    key === PLANNER_DATE_RANGE_FILTER_KEY &&
    (Array.isArray(value) ? value.length === 0 : value.trim() === '');
  const clearedAvailability =
    isClearingDateRange &&
    (hasFilterSelection(filters[PLANNER_AVAILABILITY_FILTER_KEY]) ||
      hasFilterSelection(filters[PLANNER_BILLABLE_AVAILABILITY_FILTER_KEY]));

  const isRemovingTentativeStatus =
    key === PLANNER_PROJECT_STATUS_FILTER_KEY &&
    !(Array.isArray(value) ? value : value ? [value] : []).includes(ProjectStatus.Tentative);
  const clearedProbability =
    isRemovingTentativeStatus &&
    hasFilterSelection(filters[PLANNER_PROJECT_PROBABILITY_FILTER_KEY]);

  return {
    nextFilters: {
      ...filters,
      [key]: value,
      ...(clearedAvailability && {
        [PLANNER_AVAILABILITY_FILTER_KEY]: [],
        [PLANNER_BILLABLE_AVAILABILITY_FILTER_KEY]: [],
      }),
      ...(clearedProbability && {
        [PLANNER_PROJECT_PROBABILITY_FILTER_KEY]: [],
      }),
    },
    clearedAvailability,
    clearedProbability,
  };
};
